/* ═══════════════════════════════════════════════════════════════════════
   FizzyIQ — Global header behavior
     • Promo bar carousel (auto-rotate every 6s, manual prev/next).
     • Header theme observer — flips body.header-on-light whenever the
       viewport is currently overlapping a section marked
       data-header-theme="light". Both the promo bar and .gh mirror each
       other in that state (CSS scoped under body.header-on-light).

   Self-initializes on DOMContentLoaded. Safe to load on every page —
   each subsystem no-ops when its target DOM isn't present.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {

  function initPromoCarousel() {
    var promoBar = document.getElementById('promoBar');
    if (!promoBar) return;
    var slides = promoBar.querySelectorAll('.promo-slide');

    if (slides.length <= 1) {
      // Single slide (or none) — hide the chevrons, no rotation needed
      var arrows = promoBar.querySelectorAll('.promo-arrow');
      arrows.forEach(function (a) { a.style.display = 'none'; });
      return;
    }

    var idx = 0;
    var timer = null;

    function show(i) {
      idx = (i + slides.length) % slides.length;
      slides.forEach(function (s, n) {
        s.classList.toggle('active', n === idx);
      });
    }
    function startTimer() {
      stopTimer();
      timer = setInterval(function () { show(idx + 1); }, 6000);
    }
    function stopTimer() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    var prevBtn = promoBar.querySelector('.promo-arrow.prev');
    var nextBtn = promoBar.querySelector('.promo-arrow.next');
    if (prevBtn) prevBtn.addEventListener('click', function () { show(idx - 1); startTimer(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { show(idx + 1); startTimer(); });
    startTimer();
  }

  function initHeaderTheme() {
    var headerEl = document.querySelector('.gh');
    if (!headerEl) return;
    var lightSections = document.querySelectorAll('[data-header-theme="light"]');
    var heroSections = document.querySelectorAll('[data-header-theme="hero"]');

    /* The section must cover the header's WHOLE band, not merely cross its
       bottom edge (Sam 2026-08-27). The old test — `r.top < headerBottom` —
       flipped the header the instant a dark section's top edge entered the
       header's lower edge, while the header's upper half was still over the
       previous light section. The result was a header split light-over-dark
       with the old section showing through the top. Full coverage only. */
    function coversHeader(list, headerTop, headerBottom) {
      for (var i = 0; i < list.length; i++) {
        var r = list[i].getBoundingClientRect();
        if (r.top <= headerTop + 1 && r.bottom >= headerBottom - 1) return true;
      }
      return false;
    }

    /* Relative luminance of an rgb()/rgba() string; null when effectively
       transparent (so we keep climbing to the first painted background). */
    function lumaOf(cssColor) {
      var m = cssColor && cssColor.match(/rgba?\(([^)]+)\)/);
      if (!m) return null;
      var p = m[1].split(',');
      var a = p.length > 3 ? parseFloat(p[3]) : 1;
      if (a < 0.35) return null;
      return (0.299 * (+p[0]) + 0.587 * (+p[1]) + 0.114 * (+p[2])) / 255; /* 0 dark … 1 light */
    }

    /* Is the content BEHIND a given point dark? elementsFromPoint (plural) so
       we can skip the header itself and read what it is actually sitting on;
       then climb to the first ancestor with a painted background. An explicit
       data-header-theme on the way up still wins. */
    function darkBehindPoint(x, y) {
      var stack = document.elementsFromPoint(x, y) || [];
      for (var i = 0; i < stack.length; i++) {
        var el = stack[i];
        if (el === headerEl || headerEl.contains(el)) continue; /* look past the header */
        while (el && el !== document.documentElement) {
          var flag = el.getAttribute && el.getAttribute('data-header-theme');
          if (flag === 'hero' || flag === 'dark') return true;
          if (flag === 'light') return false;
          var l = lumaOf(window.getComputedStyle(el).backgroundColor);
          if (l !== null) return l < 0.5;
          el = el.parentElement;
        }
        break;
      }
      return false; /* nothing painted found → treat as light → solid header */
    }

    /* Sample the header's OWN band — top edge and bottom edge — and go
       transparent only when BOTH are dark, i.e. the header is completely over
       the dark section. The old version sampled a single point 10px BELOW the
       header, which described the section the header was approaching rather
       than the one it was over. Solid is the safe state during the crossover:
       a solid navy bar over a light section is correct, whereas a transparent
       bar half-over one is the artifact Sam caught. */
    function underHeaderIsDark(headerTop, headerBottom) {
      /* Sampling only the centre line let one dark element under the middle of
         the bar turn the whole header transparent while the rest of it sat on
         white — the home bento's image card did exactly that, leaving white nav
         labels on a white page. Sam's rule: transparent ONLY when the entire
         background behind the bar is dark or an image. So probe across the full
         width as well as top-to-bottom, and require EVERY sample to be dark.
         Any single light sample returns early, which is also the cheap path:
         a header over a light page usually fails on the first probe. */
      var w = window.innerWidth;
      var xs = [Math.round(w * 0.04), Math.round(w * 0.28), Math.round(w / 2),
                Math.round(w * 0.72), Math.round(w * 0.96)];
      var ys = [Math.round(headerTop + 2), Math.round(headerBottom - 2)];
      for (var yi = 0; yi < ys.length; yi++) {
        for (var xi = 0; xi < xs.length; xi++) {
          if (!darkBehindPoint(xs[xi], ys[yi])) return false;
        }
      }
      return true;
    }

    var rafPending = false;
    function update() {
      rafPending = false;
      var hr = headerEl.getBoundingClientRect();
      var headerTop = hr.top, headerBottom = hr.bottom;
      // Is the content under the header DARK (→ transparent/frosted) or LIGHT
      // (→ solid navy)? Explicit data-header-theme flags win (hero/dark = dark,
      // light = light); otherwise auto-detect from the painted background. Using
      // the transparent/solid classes for every case keeps it correct on pages
      // that aren't body.fizzy-dark (where the default header is solid).
      var dark;
      if (coversHeader(heroSections, headerTop, headerBottom)) dark = true;
      else if (coversHeader(lightSections, headerTop, headerBottom)) dark = false;
      else dark = underHeaderIsDark(headerTop, headerBottom);
      document.body.classList.toggle('header-over-hero', dark);  // transparent/frosted on every page
      document.body.classList.toggle('header-on-light', !dark);  // solid navy on every page
    }
    function schedule() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  /* Floating-header pattern removed 2026-06-07 (Sam) — the header is now
     full-width on every page with no floating/inset state. */

  /* Mobile menu — hamburger (.menu-btn) toggles a drop-down panel
     (#mobileMenu) anchored beneath the header, with a separate scrim
     (#mobileMenuScrim) dimming everything below the header. Both
     elements share an .open class. Close on: (1) ESC key, (2) tap on
     the scrim, (3) any nav link click (auto-close on navigation),
     (4) tapping the hamburger again. Added 2026-05-26 after Sam
     flagged the missing handler; restructured to drop-down-beneath-
     header per follow-up. */
  function initMobileMenu() {
    var menuBtn = document.querySelector('.gh .menu-btn');
    var menu    = document.getElementById('mobileMenu');
    var scrim   = document.getElementById('mobileMenuScrim');
    if (!menuBtn || !menu) return;

    var navLinks = menu.querySelectorAll('.mm-link, .mm-cta');

    /* Pin the menu + scrim to the actual header bottom so there's no
       gap between the header and the panel regardless of the promo
       bar's state or the header's responsive height (Sam: 2026-05-26).
       Measured on every open to handle promo dismissal / resize. */
    function positionToHeader() {
      var gh = document.querySelector('.gh');
      if (!gh) return;
      var top = Math.round(gh.getBoundingClientRect().bottom);
      menu.style.top = top + 'px';
      if (scrim) scrim.style.top = top + 'px';
    }

    function open() {
      positionToHeader();
      menu.classList.add('open');
      if (scrim) scrim.classList.add('open');
      menu.setAttribute('aria-hidden', 'false');
      if (scrim) scrim.setAttribute('aria-hidden', 'false');
      menuBtn.setAttribute('aria-expanded', 'true');
      menuBtn.setAttribute('aria-label', 'Close menu');
      /* body.mobile-menu-open flips the promo bar + header from the
         frosted treatment to solid BC indigo via CSS, so the header
         and the open menu read as one continuous blue surface. */
      document.body.classList.add('mobile-menu-open');
      /* Mutually exclusive with the cart drawer (and any other overlay) —
         announce that the menu is opening so others close themselves. */
      document.dispatchEvent(new CustomEvent('fizzy:overlay-open', { detail: 'menu' }));
      /* Lock body scroll while the menu is open (Sam: 2026-05-26).
         Stash the previous overflow value so close() can restore it
         exactly, in case another component (cart drawer, modal) had
         already locked it. */
      document.body.dataset.mmPrevOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
    }
    function close() {
      menu.classList.remove('open');
      if (scrim) scrim.classList.remove('open');
      menu.setAttribute('aria-hidden', 'true');
      if (scrim) scrim.setAttribute('aria-hidden', 'true');
      menuBtn.setAttribute('aria-expanded', 'false');
      menuBtn.setAttribute('aria-label', 'Open menu');
      document.body.classList.remove('mobile-menu-open');
      /* Restore previous overflow state (might be '' or 'hidden' if
         another component is still active). */
      document.body.style.overflow = document.body.dataset.mmPrevOverflow || '';
      delete document.body.dataset.mmPrevOverflow;
    }

    menuBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (menu.classList.contains('open')) close(); else open();
    });

    /* Tapping the scrim closes the menu (standard drawer pattern). */
    if (scrim) scrim.addEventListener('click', close);

    /* Auto-close on link / CTA click so the user lands on the next
       page with a fresh state, not the menu still open. */
    navLinks.forEach(function (link) {
      link.addEventListener('click', close);
    });

    /* ESC closes the menu when it's open. */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('open')) close();
    });

    /* Close the menu if a different overlay (e.g. the cart drawer) opens —
       only one slider at a time. */
    document.addEventListener('fizzy:overlay-open', function (e) {
      if (e && e.detail !== 'menu' && menu.classList.contains('open')) close();
    });
  }

  /* Mobile menu tabs (Shop / Science / Explore / Sign In) — clicking a tab
     shows its panel and hides the others. */
  function initMobileTabs() {
    var menu = document.getElementById('mobileMenu');
    if (!menu) return;
    var tabs = menu.querySelectorAll('.mm-tab');
    var panels = menu.querySelectorAll('.mm-panel');
    if (!tabs.length || !panels.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var key = tab.getAttribute('data-mm-tab');
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-mm-panel') === key);
        });
        var panels_wrap = menu.querySelector('.mm-panels');
        if (panels_wrap) panels_wrap.scrollTop = 0;
      });
    });
  }

  /* Publish the header's real height as --gh-h so flush full-bleed heroes
     can clear it exactly (Sam 2026-08-26: image heroes sit flush under the
     header; text-led pages keep breathing room). */
  function publishHeight() {
    var gh = document.querySelector('.gh');
    if (gh) document.documentElement.style.setProperty('--gh-h', gh.offsetHeight + 'px');
  }

  function init() {
    initPromoCarousel();
    initHeaderTheme();
    initMobileMenu();
    initMobileTabs();
    publishHeight();
    window.addEventListener('resize', publishHeight, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
